import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doctorService } from '@/services/api';
import { useClinic } from '@/contexts/ClinicContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { formatDoctorName } from '@/lib/utils';
import {
  ArrowLeft,
  Stethoscope,
  Save,
  Loader2,
  Mail,
  Phone,
  User,
  Award,
  DollarSign,
  Building2,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SPECIALIZATIONS = [
  'General Medicine',
  'Cardiology',
  'Pediatrics',
  'Orthopedics',
  'Dermatology',
  'Gynecology & Obstetrics',
  'Neurology',
  'ENT (Otolaryngology)',
  'Ophthalmology',
  'Pulmonology',
  'General Surgery',
];

const doctorSchema = z.object({
  email: z.string().email('Valid mandatory email is required (used for communication & access)'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().min(8, 'Phone number is required'),
  specialization: z.string().min(1, 'Specialization is required'),
  qualification: z.string().min(1, 'Qualification is required (e.g. MBBS, MD)'),
  registrationNo: z.string().min(1, 'MCI / NMC Registration Number is required'),
  consultationFee: z.coerce.number().min(0, 'Fee must be positive'),
  clinicId: z.string().min(1, 'Assigned Clinic is required'),
  dutySchedule: z.string().default('Mon-Sat 09:00-17:00'),
  status: z.string().default('active'),
});

const DoctorFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { hospitalId, activeClinicId, availableClinics, buildUrl } = useClinic();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const form = useForm({
    resolver: zodResolver(doctorSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      specialization: 'General Medicine',
      qualification: 'MBBS, MD',
      registrationNo: '',
      consultationFee: 500,
      clinicId: activeClinicId || availableClinics[0]?.id || '',
      dutySchedule: 'Mon-Sat 09:00-17:00',
      status: 'active',
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchDoctor = async () => {
        try {
          setLoading(true);
          const data = await doctorService.getById(id, hospitalId);
          form.reset({
            email: data.email || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            specialization: data.specialization || 'General Medicine',
            qualification: data.qualification || '',
            registrationNo: data.registrationNo || data.licenseNumber || '',
            consultationFee: data.consultationFee || 500,
            clinicId: data.assignedClinics?.[0] || activeClinicId || availableClinics[0]?.id || '',
            dutySchedule: data.dutySchedule || 'Mon-Sat 09:00-17:00',
            status: data.status || 'active',
          });
        } catch (err) {
          toast.error('Failed to load doctor profile');
          navigate(buildUrl('/doctors'));
        } finally {
          setLoading(false);
        }
      };
      fetchDoctor();
    }
  }, [id, isEdit, form, hospitalId, navigate, buildUrl, availableClinics]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const autoUsername = (data.email ? data.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').slice(0, 30) : '') || 'doctor';
      const payload = {
        name: formatDoctorName(`${data.firstName} ${data.lastName}`),
        username: autoUsername.toLowerCase(),
        email: data.email.toLowerCase(),
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        specialization: data.specialization,
        qualification: data.qualification,
        licenseNumber: data.registrationNo,
        registrationNumber: data.registrationNo,
        consultationFee: data.consultationFee,
        assignedClinics: [data.clinicId],
        dutySchedule: data.dutySchedule,
        status: data.status,
        isActive: data.status === 'active',
      };

      if (isEdit) {
        await doctorService.update(id, payload, hospitalId);
        toast.success(`${formatDoctorName(`${data.firstName} ${data.lastName}`)} updated successfully`);
      } else {
        await doctorService.create(payload, hospitalId);
        toast.success(`${formatDoctorName(`${data.firstName} ${data.lastName}`)} onboarded successfully!`);
      }

      navigate(buildUrl('/doctors'));
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save doctor details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(buildUrl('/doctors'))}
          className="text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Doctor Directory
        </Button>
        <div className="h-4 w-px bg-slate-200" />
        <h1 className="text-xl font-heading font-bold text-slate-900">
          {isEdit ? 'Edit Physician Profile' : 'Onboard Physician / Clinical Specialist'}
        </h1>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal & Login Credentials */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-teal-600" />
              Doctor Identity & Contact Details
            </CardTitle>
            <CardDescription className="text-xs">
              Personal name and professional contact email and phone number
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" placeholder="e.g. Rajesh" {...form.register('firstName')} />
                {form.formState.errors.firstName && (
                  <p className="text-xs text-red-500">{form.formState.errors.firstName.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input id="lastName" placeholder="e.g. Kumar" {...form.register('lastName')} />
                {form.formState.errors.lastName && (
                  <p className="text-xs text-red-500">{form.formState.errors.lastName.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Mandatory Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="dr.rajesh@apollohospital.in"
                    className="pl-9"
                    {...form.register('email')}
                  />
                </div>
                {form.formState.errors.email && (
                  <p className="text-xs text-red-500">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone">Contact Phone *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="phone" placeholder="+91 98765 43210" className="pl-9" {...form.register('phone')} />
                </div>
                {form.formState.errors.phone && (
                  <p className="text-xs text-red-500">{form.formState.errors.phone.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinical Credentials & Specialization */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-4 w-4 text-teal-600" />
              Medical Council License & Specialization
            </CardTitle>
            <CardDescription className="text-xs">
              MCI/NMC registration number, clinical department, and academic qualifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="registrationNo">MCI / NMC Registration Number *</Label>
                <Input
                  id="registrationNo"
                  placeholder="e.g. MCI/2018/84729"
                  className="font-mono uppercase"
                  {...form.register('registrationNo')}
                />
                {form.formState.errors.registrationNo && (
                  <p className="text-xs text-red-500">{form.formState.errors.registrationNo.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="specialization">Medical Specialization *</Label>
                <Select
                  value={form.watch('specialization')}
                  onValueChange={(val) => form.setValue('specialization', val)}
                >
                  <SelectTrigger id="specialization">
                    <SelectValue placeholder="Select Specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALIZATIONS.map((spec) => (
                      <SelectItem key={spec} value={spec}>
                        {spec}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="qualification">Medical Qualifications *</Label>
                <Input
                  id="qualification"
                  placeholder="e.g. MBBS, MD (Cardiology), DM"
                  {...form.register('qualification')}
                />
                {form.formState.errors.qualification && (
                  <p className="text-xs text-red-500">{form.formState.errors.qualification.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="consultationFee">Consultation Fee (₹ INR) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-xs">₹</span>
                  <Input
                    id="consultationFee"
                    type="number"
                    placeholder="800"
                    className="pl-7"
                    {...form.register('consultationFee')}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinic OPD Assignment & Duty Schedule */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-teal-600" />
              Clinic Assignment & Duty Schedule
            </CardTitle>
            <CardDescription className="text-xs">
              Assign outpatient unit counter and weekly shift timings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="clinicId">Primary OPD Clinic Assignment *</Label>
                <Select
                  value={form.watch('clinicId')}
                  onValueChange={(val) => form.setValue('clinicId', val)}
                >
                  <SelectTrigger id="clinicId">
                    <SelectValue placeholder="Select Clinic Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableClinics?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({c.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dutySchedule">Weekly Duty Timings</Label>
                <Input
                  id="dutySchedule"
                  placeholder="e.g. Mon-Sat 09:00-17:00"
                  {...form.register('dutySchedule')}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="status">Physician Status</Label>
                <Select
                  value={form.watch('status')}
                  onValueChange={(val) => form.setValue('status', val)}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (Available for OPD)</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(buildUrl('/doctors'))}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving Doctor...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? 'Save Changes' : 'Onboard Doctor to Hospital'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DoctorFormPage;

